// Lišta příkazů (koncepce A, postup 60) – jeden řádek místo pásu karet; vzor Windows 11 Průzkumník a GNOME.
//  • pořadí vždy stejné: hlavní akce (plné tlačítko) · akce nad výběrem · ostatní akce · ⋯
//  • akce, které pro výběr nedávají smysl, se schovají (ne zašedí); méně časté (place 'more') jsou rovnou v ⋯
//  • co se do šířky nevejde, přeteče do ⋯ (zprava, hlavní akce zůstává) – žádné vodorovné posouvání
//  • zobrazení a nastavení (place 'view') si pohled dává pod vlastní ⚙ (viewMenu)
import { h, clear } from '../dom';
import { t } from '../i18n';
import { icon } from '../icons';
import { IwMenu, type MenuItem } from './menu';

export type BarPlace = 'primary' | 'selection' | 'action' | 'more' | 'view';

export interface BarItem {
  id: string;
  label: string;
  icon?: string | null;
  shortcut?: string | null;
  place: BarPlace;
  danger?: boolean;
  disabled?: boolean;
  toggle?: boolean;
  active?: boolean;
  /** rozbalovací tlačítko (Přesunout ▾…) */
  menu?: MenuItem[];
  onClick?: () => void;
  /** oddíl v ⋯: akce nad výběrem, nebo celé okno / aplikace (výchozí podle place) */
  scope?: 'selection' | 'global';
  /** na liště jen ikona (‹ › v Kalendáři); v nabídkách s popiskem */
  iconOnly?: boolean;
}

/** Položka lišty jako položka nabídky (⋯, ⚙, ⋮ na telefonu). */
export function barMenuItem(b: BarItem): MenuItem {
  return { id: b.id, label: b.label, icon: b.icon ?? null, shortcut: b.shortcut ?? null, danger: b.danger, disabled: b.disabled, checked: b.toggle ? Boolean(b.active) : undefined, children: b.menu, onSelect: b.menu ? undefined : () => b.onClick?.() };
}

/** Viditelná na liště? Akce nad výběrem jen povolené; 'more' a 'view' nikdy (jsou v ⋯ / ⚙). */
export function barShown(b: BarItem): boolean {
  if (b.place === 'more' || b.place === 'view') return false;
  return b.place !== 'selection' || !b.disabled;
}

export class IwCommandBar extends HTMLElement {
  private items: BarItem[] = [];
  private row = h('div', { class: 'cbar-row' });
  private moreBtn = h('button', { type: 'button', class: 'cbar-btn cbar-more', title: t('core.ui.more') }, icon('more')) as HTMLButtonElement;
  private overflowed: BarItem[] = [];
  /** nadpis oddílu celého okna v ⋯ (název aplikace, např. Kontakty) */
  globalLabel = '';
  /** Příkazy hostitele na konec ⋯ (konzole správy: obnovit, nové okno, sbalit strom) – jedny tři tečky v záhlaví. */
  private extra: { label: string; items: () => MenuItem[] } | null = null;
  /** Příkazy hostitele (konzole) – i pro ⋮ na telefonu. */
  extraItems(): { label: string; items: MenuItem[] } | null {
    return this.extra ? { label: this.extra.label, items: this.extra.items() } : null;
  }

  setExtraMore(extra: { label: string; items: () => MenuItem[] } | null): void {
    this.extra = extra;
    this.render();
  }
  private ro: ResizeObserver | null = null;

  connectedCallback(): void {
    if (!this.hasChildNodes()) {
      this.append(this.row);
      this.moreBtn.addEventListener('click', () => IwMenu.showAt(this.moreItems(), this.moreBtn));
    }
    this.ro ??= new ResizeObserver(() => this.fit());
    this.ro.observe(this);
    this.render();
  }

  disconnectedCallback(): void {
    this.ro?.disconnect();
    this.ro = null;
  }

  setItems(items: BarItem[]): void {
    this.items = items;
    this.render();
  }

  all(): BarItem[] {
    return this.items;
  }

  /** Povolení podle výběru – akce nad výběrem se tím ukážou / schovají. */
  setDisabled(pred: (b: BarItem) => boolean): void {
    for (const b of this.items) b.disabled = pred(b);
    this.render();
  }

  /** Obsah ⋯ v oddílech: Vybrané (co se nevešlo + akce nad výběrem), celé okno (méně časté), Zobrazení. */
  private moreItems(): MenuItem[] {
    const scope = (b: BarItem): 'selection' | 'global' => b.scope ?? (b.place === 'selection' || b.id.startsWith('r:') ? 'selection' : 'global');
    const pool = [...this.overflowed, ...this.items.filter((b) => b.place === 'more' && !b.disabled)]; // co teď nejde, se schová (ne zašedne)
    const sel = pool.filter((b) => scope(b) === 'selection');
    const glob = pool.filter((b) => scope(b) === 'global');
    const out: MenuItem[] = [];
    if (sel.length) out.push({ header: true, label: t('core.ui.selected_items') }, ...sel.map(barMenuItem));
    if (glob.length) {
      if (out.length) out.push({ separator: true, label: '' });
      if (this.globalLabel) out.push({ header: true, label: this.globalLabel });
      out.push(...glob.map(barMenuItem));
    }
    // úzké okno: ⚙ v záhlaví není – Zobrazení jako podnabídka
    const view = this.items.filter((b) => b.place === 'view');
    if (view.length && this.closest('iw-window')?.classList.contains('narrow')) {
      out.push({ separator: true, label: '' }, { header: true, label: t('core.ui.view_settings') }, ...view.map(barMenuItem));
    }
    const ex = this.extra?.items() ?? [];
    if (ex.length) {
      const has = new Set(out.map((m) => m.id).filter(Boolean));
      const refresh = (id: string | undefined): boolean => id === '__refresh' || id === 'refresh';
      // stejný příkaz (Obnovit) už lišta má – ne podruhé
      const add = ex.filter((m) => !m.id || (!has.has(m.id) && !this.items.some((b) => b.id === m.id || (refresh(m.id) && refresh(b.id)))));
      if (add.length) out.push(...(out.length ? [{ separator: true, label: '' }] : []), { header: true, label: this.extra?.label ?? '' }, ...add);
    }
    return out;
  }

  private render(): void {
    if (!this.isConnected) return;
    clear(this.row);
    let prevPlace: BarPlace | null = null;
    // pořadí vždy podle místa (hlavní · nad výběrem · ostatní), ne podle pořadí deklarace v okně
    const rank: Record<BarPlace, number> = { primary: 0, selection: 1, action: 2, more: 3, view: 4 };
    const shown = this.items.filter(barShown).map((b, i) => ({ b, i })).sort((x, y) => rank[x.b.place] - rank[y.b.place] || x.i - y.i).map((x) => x.b);
    for (const b of shown) {
      if (prevPlace && prevPlace !== b.place && b.place !== 'selection') this.row.appendChild(h('span', { class: 'cbar-sep' }));
      if (prevPlace === 'primary' && b.place === 'selection') this.row.appendChild(h('span', { class: 'cbar-sep' }));
      prevPlace = b.place;
      this.row.appendChild(this.button(b));
    }
    this.row.appendChild(this.moreBtn);
    this.fit();
    requestAnimationFrame(() => this.fit()); // záhlaví se mohlo právě postavit (šířka ještě nebyla konečná)
  }

  /** Přetečení: zobrazit vše, pak zprava schovávat, dokud se řádek nevejde (hlavní akce zůstává vždy). */
  private fit(): void {
    const btns = Array.from(this.row.querySelectorAll<HTMLElement>('.cbar-btn[data-id]'));
    const seps = Array.from(this.row.querySelectorAll<HTMLElement>('.cbar-sep'));
    for (const el of [...btns, ...seps]) el.hidden = false;
    this.moreBtn.hidden = false; // měřit i s ⋯, ať se na konec vejde
    this.overflowed = [];
    const width = this.clientWidth;
    if (width > 0) {
      for (let i = btns.length - 1; i >= 0 && this.row.scrollWidth > width + 1; i--) {
        const el = btns[i] as HTMLElement;
        const b = this.items.find((x) => x.id === el.dataset['id']);
        if (!b || b.place === 'primary') continue;
        el.hidden = true;
        this.overflowed.unshift(b);
        const prev = el.previousElementSibling as HTMLElement | null;
        if (prev?.classList.contains('cbar-sep')) prev.hidden = true;
      }
    }
    this.moreBtn.hidden = !this.extra && !this.overflowed.length && !this.items.some((b) => b.place === 'more' || (b.place === 'view' && this.closest('iw-window')?.classList.contains('narrow')));
  }

  private button(b: BarItem): HTMLElement {
    const btn = h(
      'button',
      {
        type: 'button',
        class: 'cbar-btn' + (b.place === 'primary' ? ' primary' : '') + (b.danger ? ' danger' : '') + (b.active ? ' active' : '') + (b.iconOnly && b.icon ? ' icon-only' : ''),
        'aria-label': b.iconOnly ? b.label : null,
        dataset: { id: b.id },
        title: b.shortcut ? `${b.label} (${b.shortcut})` : b.label,
        'aria-pressed': b.toggle ? (b.active ? 'true' : 'false') : null,
      },
      b.icon ? icon(b.icon) : null,
      h('span', { class: 'cbar-label' }, b.label),
      b.menu ? icon('chevron-down', 'icon cbar-caret') : null,
    ) as HTMLButtonElement;
    btn.disabled = Boolean(b.disabled);
    btn.addEventListener('click', () => {
      if (b.menu) IwMenu.showAt(b.menu, btn);
      else b.onClick?.();
    });
    return btn;
  }
}

customElements.define('iw-commandbar', IwCommandBar);

/** Kulaté tlačítko v záhlaví (☰, ←, lupa, ⚙, ⋮). */
export function headButton(iconName: string, title: string, onClick: (btn: HTMLButtonElement) => void, cls = ''): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'head-btn' + (cls ? ' ' + cls : ''), title, 'aria-label': title }, icon(iconName)) as HTMLButtonElement;
  b.addEventListener('click', () => onClick(b));
  return b;
}

/** Hodnoty záznamu do šablony odkazu: 'tel:{phone}'. Prázdná hodnota = null (odkaz nemá smysl). */
export function fillHref(template: string, data: Record<string, unknown>): string | null {
  let empty = false;
  const out = template.replace(/\{(\w+)\}/g, (_, k: string) => {
    const v = data[k];
    if (v === null || v === undefined || v === '') empty = true;
    return encodeURIComponent(String(v ?? '')).replace(/%2B/g, '+').replace(/%40/g, '@');
  });
  return empty ? null : out;
}
